import React, { forwardRef } from "react";

import _ from "lodash";

import { getEnvironment, isFeatureActiveOnCurrentFarm, useTranslation } from "utils";

import { FEATURE_FLAG_SLUGS } from "consts";

import {
  ACCOUNTING_METHODOLOGY,
  AccountingMethodology,
  EDITING_REQUEST_TYPE,
  FERTILIZER_TYPE,
  OrganicAndMineralFertilizersProps,
  PRACTICE_SLUG,
  PracticeBundle,
  PracticeFCPO,
  VerificationEditingRequest,
  ZERO_UUID,
} from "models";

import { AlertBanner, Divider, Flex, Form, Icon, IconLinkButton, Link, Spin, Switch, Tag, Tooltip, TooltipWrapper, Trans, Typography } from "components";

import { CustomKlimRadioGroupWithPayouts } from "pages/app/all-field-view/components/forms/fertilizers/CustomKlimRadioGroupWithPayouts";
import { OrganicOrMineralFertilizerForm } from "pages/app/all-field-view/components/forms/fertilizers/OrganicOrMineralFertilizerForm";
import { useFertilizerForm } from "pages/app/all-field-view/hooks/useFertilizerForm";
import { isRequired, isRequiredForSaving } from "pages/app/all-field-view/utils/getInputRequirementConfig";
import { EditingRequestBanner } from "pages/app/field-timeline/components/EditingRequestBanner";

// Maps a fertilizer type to the (singular) editing-request type it targets
const FERTILIZER_TYPE_TO_REQUEST_TYPE = {
  [FERTILIZER_TYPE.MINERAL]: EDITING_REQUEST_TYPE.MINERAL_FERTILIZER,
  [FERTILIZER_TYPE.ORGANIC]: EDITING_REQUEST_TYPE.ORGANIC_FERTILIZER,
} as const;

/**
 * Custom hook to encapsulate Soil Plus 3 feature logic for mineral fertilizers
 */
const useMineralFertilizerSoilPlus3 = (
  chosenFertilizerType: string,
  existingFertilizers: PracticeFCPO[],
  isFertilizerTypeApplied: boolean | null,
  practiceBundle?: PracticeBundle,
  carbonProgramMethodology?: AccountingMethodology
) => {
  const isSoilPlus3DataGapFeatureActive = isFeatureActiveOnCurrentFarm(FEATURE_FLAG_SLUGS.SOIL_PLUS_3_DATA_GAP);
  const isMineralType = chosenFertilizerType === FERTILIZER_TYPE.MINERAL;
  const hasFertilizers = existingFertilizers.some((fertilizer) => fertilizer.assignedPracticeId !== ZERO_UUID);
  const isInventoryProgram = carbonProgramMethodology === ACCOUNTING_METHODOLOGY.inventory;

  return {
    shouldShowExtraPayoutTag: isSoilPlus3DataGapFeatureActive && isMineralType && hasFertilizers && isFertilizerTypeApplied && practiceBundle?.active,
    shouldShowOptionalControls: isSoilPlus3DataGapFeatureActive && isMineralType,
    shouldShowInfoBanner: isSoilPlus3DataGapFeatureActive && isMineralType,
    shouldHideMainContent: hasFertilizers ? false : isSoilPlus3DataGapFeatureActive && isMineralType && !practiceBundle?.active,
    // Also relevant outside of Soil+ 3.0 feature
    shouldHideFertilizerSection: hasFertilizers ? false : isInventoryProgram && isMineralType && !practiceBundle?.active,
  };
};

export const AddEditOrganicAndMineralFertilizers = forwardRef<HTMLElement, OrganicAndMineralFertilizersProps>(
  (
    {
      currency,
      chosenFertilizerType,
      disabled,
      existingFertilizers,
      baselineYears,
      recommendedSeedingDate,
      year,
      requirementLevel,
      requirementConfig,
      practiceConfigForBulk,
      practiceBundle,
      onBundleActivation,
      bulkEditMode,
      renderEntryTag,
      renderEntryTitle,
      isReadOnly,
      carbonProgramMethodology,
      fieldIsEnrolled,
      editingRequests = [],
      isSubmissionFeatureEnabled = false,
      farmId,
    }: OrganicAndMineralFertilizersProps,
    ref
  ) => {
    const { t } = useTranslation(["modules", "allFieldView"]);

    const {
      isFertilizerTypeApplied,
      fertilizerApplications,
      handleFertilizerTypeChange,
      handleRemoveFertilizerApplication,
      addNewFertilizerApplication,
      updateFertilizerApplication,
      isFertilizerLoading,
      fertilizers,
      toggleDisabled,
      hasMixedValues,
    } = useFertilizerForm(chosenFertilizerType, existingFertilizers, disabled, practiceConfigForBulk);

    const editingRequestType = FERTILIZER_TYPE_TO_REQUEST_TYPE[chosenFertilizerType];
    const getFertilizerEditingRequest = (id: string): VerificationEditingRequest | undefined =>
      editingRequests.find(
        (request) =>
          request.requestType === editingRequestType &&
          (editingRequestType === EDITING_REQUEST_TYPE.MINERAL_FERTILIZER ? request.fieldMineralFertilizerId === id : request.fieldOrganicFertilizerId === id)
      );
    const noneAppliedApplication = existingFertilizers.find((fertilizer) => fertilizer.assignedPracticeId === ZERO_UUID);

    // Use custom hook for Soil Plus 3 feature logic
    const { shouldShowExtraPayoutTag, shouldShowOptionalControls, shouldShowInfoBanner, shouldHideMainContent, shouldHideFertilizerSection } =
      useMineralFertilizerSoilPlus3(chosenFertilizerType, existingFertilizers, isFertilizerTypeApplied, practiceBundle, carbonProgramMethodology);

    const fertilizerApplicationWithYear = fertilizerApplications.find((fertilizer) => fertilizer.year !== undefined);

    const isBaseline = fertilizerApplicationWithYear?.year ? baselineYears?.includes(fertilizerApplicationWithYear.year) : baselineYears?.includes(year);

    return (
      <>
        {shouldHideFertilizerSection ? null : (
          <Flex
            vertical
            className="fertilizers"
            data-testid={`add-edit-${chosenFertilizerType === FERTILIZER_TYPE.ORGANIC ? "organic" : "mineral"}-fertilizer-form`}
            ref={ref}
          >
            <div className="fertilizers-header-container">
              {/* Header */}
              <div className="margin-bottom-100">
                <Flex justify="space-between">
                  <Flex gap="0.62rem">
                    <Typography variant="h5" className="fertilizers-title">
                      {" "}
                      {t(`allFieldView:fertilizerForm.mainTitle.${chosenFertilizerType}`)}{" "}
                    </Typography>
                    {isBaseline && (
                      <Tag
                        dataTestId={`timeline-drawer-${chosenFertilizerType}-form-baseline-tag`}
                        className="chip-pill capitalize margin-0"
                        colour="grey"
                        text={t("allFieldView:fertilizerForm.baseline")}
                      />
                    )}
                    {shouldShowExtraPayoutTag && (
                      <Tag
                        dataTestId={`timeline-drawer-${chosenFertilizerType}-form-extra-payout-tag`}
                        className="chip-pill capitalize margin-0"
                        colour="blue"
                        text={t("allFieldView:fertilizerForm.extraPayout")}
                      />
                    )}
                  </Flex>
                  {shouldShowOptionalControls ? (
                    <Flex align="center" gap=".62rem">
                      <Tooltip placement="top" title={t("allFieldView:fertilizerForm.mineralFertiliserInfo")}>
                        <IconLinkButton iconType="info" theme="grey" />
                      </Tooltip>
                      <Tag
                        dataTestId={`timeline-drawer-${chosenFertilizerType}-form-optional-tag`}
                        className="chip-pill capitalize margin-0"
                        colour="grey"
                        text={t("allFieldView:fertilizerForm.optional")}
                      />
                      <TooltipWrapper
                        tooltip={
                          !practiceBundle?.enrollable || practiceBundle?.hasConfirmedData || !fieldIsEnrolled
                            ? {
                                title: t(
                                  `allFieldView:fertilizerForm.mineralFertiliserTooltip.${
                                    !practiceBundle?.enrollable ? "notEnrollable" : practiceBundle?.hasConfirmedData ? "hasConfirmedData" : "fieldNotEnrolled"
                                  }`
                                ),
                              }
                            : null
                        }
                      >
                        {/* could be isMineralActivated or isOrganicActivated */}
                        <Form.Item name={`is${_.upperFirst(chosenFertilizerType)}Activated`} initialValue={practiceBundle?.active}>
                          <Switch
                            checked={practiceBundle?.active}
                            onClick={(active) => onBundleActivation?.(PRACTICE_SLUG.MINERAL_FERTILIZERS, active)}
                            disabled={!practiceBundle?.enrollable || practiceBundle?.hasConfirmedData || !fieldIsEnrolled}
                          />
                        </Form.Item>
                      </TooltipWrapper>
                    </Flex>
                  ) : (
                    <Form.Item name={`is${_.upperFirst(chosenFertilizerType)}Activated`} initialValue={practiceBundle?.active} hidden={true}>
                      {/* we need this to prevent an antd form error; Form.Item needs a valid child */}
                      <div />
                    </Form.Item>
                  )}
                </Flex>
                {shouldShowInfoBanner && (
                  <AlertBanner
                    className="margin-top-200 margin-bottom-200"
                    description={<Trans i18nKey={"allFieldView:fertilizerForm.mineralFertiliserInfoBanner"} components={{ strong: <strong /> }} />}
                    type="recommended"
                    iconName="tabler/bulb"
                    iconAlign="start"
                  />
                )}
              </div>
              {/* Body */}
              {!shouldHideMainContent && (
                <>
                  <Flex className="row-space-between" justify="space-between" align="center" gap="1rem">
                    <Typography variant="paragraph-m">
                      {t(`allFieldView:fertilizerForm.radioTitle.${bulkEditMode ? `${chosenFertilizerType}Bulk` : chosenFertilizerType}`)}{" "}
                      {isRequired(requirementLevel) && "*"}
                    </Typography>
                    <Form.Item
                      name={`is${_.upperFirst(chosenFertilizerType)}Applied`} // could be isMineralApplied or isOrganicApplied
                      initialValue={isFertilizerTypeApplied}
                      rules={[{ required: isRequiredForSaving(requirementLevel), message: t("allFieldView:fertilizerForm.fertilizerRequired") }]}
                      help={hasMixedValues && bulkEditMode ? t("allFieldView:tillageForm.helperTextYesNoQuestion") : undefined}
                    >
                      <CustomKlimRadioGroupWithPayouts
                        value={isFertilizerTypeApplied}
                        currency={currency}
                        onChange={(e) => {
                          handleFertilizerTypeChange(e.target.value);
                        }}
                        dataTestId="fertilizer-radio-group"
                        className="fertilizers-radio"
                        disabled={toggleDisabled}
                      />
                    </Form.Item>
                  </Flex>
                  {practiceConfigForBulk?.showMessage && (
                    <AlertBanner
                      className="margin-top-200 margin-bottom-200"
                      description={<Trans i18nKey={"allFieldView:cropForm.multiplePracticesMessage"} components={{ strong: <strong /> }} />}
                      type="info-highlighted"
                      iconName="tabler/info-circle"
                      iconAlign="start"
                    />
                  )}
                  {isSubmissionFeatureEnabled && farmId && !!noneAppliedApplication?.id && (
                    <FertilizerEditingRequest
                      farmId={farmId}
                      id={noneAppliedApplication.id}
                      type={editingRequestType}
                      editingRequest={getFertilizerEditingRequest(noneAppliedApplication.id)}
                    />
                  )}
                </>
              )}
            </div>
            {isFertilizerTypeApplied && (
              <>
                <Divider />
                <div className="fertilizers-body-container">
                  {isFertilizerLoading ? (
                    <Flex justify="center">
                      <Spin />
                    </Flex>
                  ) : (
                    fertilizerApplications.map((application, index) => (
                      <OrganicOrMineralFertilizerForm
                        key={`${chosenFertilizerType}-fertilizer-${application.id}-${index}`}
                        index={index}
                        chosenFertilizerType={chosenFertilizerType}
                        fertilizers={fertilizers}
                        existingFertilizerApplication={application}
                        onRemove={() => handleRemoveFertilizerApplication(index)}
                        onUpdate={updateFertilizerApplication}
                        recommendedSeedingDate={recommendedSeedingDate}
                        currency={currency}
                        requirementConfig={requirementConfig}
                        renderEntryTag={renderEntryTag}
                        renderEntryTitle={renderEntryTitle}
                        extraContent={
                          isSubmissionFeatureEnabled &&
                          farmId &&
                          !!application.id && (
                            <FertilizerEditingRequest
                              farmId={farmId}
                              id={application.id}
                              type={editingRequestType}
                              editingRequest={getFertilizerEditingRequest(application.id)}
                            />
                          )
                        }
                        isReadOnly={isReadOnly}
                      />
                    ))
                  )}
                  <Flex justify="flex-start" className="margin-top-150 margin-bottom-50">
                    <Link
                      dataTestId={`fertilizer-add-new-${chosenFertilizerType}`}
                      onClick={addNewFertilizerApplication}
                      className="fertilizers-add-new-btn"
                      disabled={disabled}
                    >
                      <Icon iconName="klim/plus" className="icon inline" /> {t(`allFieldView:fertilizerForm.add.${chosenFertilizerType}`)}
                    </Link>
                  </Flex>
                </div>
              </>
            )}
          </Flex>
        )}
      </>
    );
  }
);

const FertilizerEditingRequest = ({
  farmId,
  id,
  type,
  editingRequest,
}: {
  farmId: string;
  id: string;
  type: typeof EDITING_REQUEST_TYPE.MINERAL_FERTILIZER | typeof EDITING_REQUEST_TYPE.ORGANIC_FERTILIZER;
  editingRequest: VerificationEditingRequest | undefined;
}) => {
  if (!editingRequest || getEnvironment().admin) {
    return null;
  }

  return <EditingRequestBanner type={type} farmId={farmId} id={id} requestId={editingRequest.id} />;
};
